import { apiRequestPaginated } from './client';
import type { AccountStatus, Address, Customer, Review } from '../types';

type ApiUser = {
  _id: string;
  name?: string | null;
  email: string;
  phone?: string | null;
  profilePicture?: string | null;
  createdAt?: string;
  addresses?: Array<{
    _id?: string;
    label?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    fullAddress?: string;
    isDefault?: boolean;
  }>;
};

type ApiReview = {
  _id: string;
  rating?: number;
  review?: string;
  createdAt?: string;
  provider?: { name?: string | null } | null;
  job?: { _id?: string; title?: string } | null;
};

const mapAddresses = (addresses: ApiUser['addresses'] = []): Address[] =>
  (addresses || []).map((addr, index) => ({
    id: addr._id || `addr-${index}`,
    label: addr.label || 'Address',
    fullAddress:
      addr.fullAddress ||
      [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ') ||
      '—',
    city: addr.city || '—',
    isDefault: Boolean(addr.isDefault),
  }));

export const mapApiUserToCustomer = (user: ApiUser): Customer => ({
  id: user._id,
  name: user.name?.trim() || '—',
  email: user.email,
  phone: user.phone || '—',
  avatar: user.profilePicture || undefined,
  registrationDate: user.createdAt || '',
  status: 'Active' as AccountStatus,
  addresses: mapAddresses(user.addresses),
});

const mapApiReview = (review: ApiReview, customerName: string): Review => ({
  id: review._id,
  jobId: review.job?._id || '',
  reviewer: customerName,
  reviewerType: 'Customer',
  reviewedParty: review.provider?.name || 'Provider',
  reviewedPartyType: 'Provider',
  rating: review.rating || 0,
  text: review.review || '',
  date: review.createdAt || '',
  status: 'Active',
});

export const usersApi = {
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ users: Customer[]; total: number }> => {
    const page = params?.page || 1;
    const limit = params?.limit || 10;
    const search = params?.search?.trim() || '';
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) query.set('search', search);

    const { data, pagination } = await apiRequestPaginated<{ users: ApiUser[] }>(
      `/admin/user/get-users?${query.toString()}`,
      { method: 'GET', auth: true }
    );

    return {
      users: (data.users || []).map(mapApiUserToCustomer),
      total: pagination.totalItems || 0,
    };
  },

  getUserById: async (id: string): Promise<Customer> => {
    // get-user-details is commented out on backend; resolve from list endpoints.
    let page = 1;
    const limit = 50;
    while (page <= 20) {
      const { users, total } = await usersApi.getUsers({ page, limit });
      const found = users.find((u) => u.id === id);
      if (found) return found;
      if (page * limit >= total) break;
      page += 1;
    }
    throw new Error('User not found');
  },

  getUserReviews: async (
    userId: string,
    customerName: string,
    params?: { page?: number; limit?: number }
  ): Promise<Review[]> => {
    const page = params?.page || 1;
    const limit = params?.limit || 50;
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const { data } = await apiRequestPaginated<{ reviews: ApiReview[] }>(
      `/admin/user/get-user-reviews/${userId}?${query.toString()}`,
      { method: 'GET', auth: true }
    );

    return (data.reviews || []).map((review) => mapApiReview(review, customerName));
  },
};
